import React from 'react';
import { ZODIAC_BADGE_TYPE_VALUES } from './avatarDesignerZodiac';

const API = process.env.REACT_APP_API_URL || '';
const DEFAULT_BUBBLE_COLOR_HEX = '282c34';

/** credit_products.id for UNLOCK: Statistics (user_credit_purchases gate for /tilastot). */
export const STATISTICS_UNLOCK_PRODUCT_ID = 'tier1-unlock-statistics';

/** credit_products.id for UNLOCK: Advanced Statistics (“Statistics+” / päivittäiset kysymykset ym.). */
export const STATISTICS_ADVANCED_UNLOCK_PRODUCT_ID = 'tier3-unlock-advanced-statistics';

/** credit_products.id for OSTA AVATAR IKONI (prerequisite for teemapaketit + settings avatar icon picker). */
export const AVATAR_IKONI_PRODUCT_ID = 'tier1-osta-avatar-ikoni';

/** credit_products.id for UNLOCK: "Game Host"-moodi — required to create/switch events to type "peli". */
export const GAME_HOST_UNLOCK_PRODUCT_ID = 'tier4-unlock-game-host';

/** credit_products.id for UNLOCK: levyraati host — create/switch events to type "levyraati" (see eventAreaUnlockCreditProductId on server). */
export const LEVYRAATI_HOST_UNLOCK_PRODUCT_ID = 'tier4-unlock-levyraati';

/** credit_products.id — trivia-ehdotus (legacy constant; duplikaatti-hyvitys ym.). */
export const TRIVIA_SUGGESTION_CREDIT_PRODUCT_ID = 'tier1-ehdota-trivia';

/** Peliehdotustuotteet: osto luo rivin + DM-linkin (?gameSuggestion=UUID), ei uudelleenohjausta kaupasta. */
export const GAME_SUGGESTION_CREDIT_PRODUCT_IDS = new Set([
  'tier1-ehdota-trivia',
  'tier1-ehdota-arvoitus',
  'tier1-ehdota-arvoitusta-l3',
  'tier2-ehdota-abcd',
  'tier2-ehdota-emoji-kuva',
]);

/** Päivän kysymys — DM + /submitDaily -lomake (ei game_suggestions). */
export const PAIVAN_KYSYMYS_CREDIT_PRODUCT_ID = 'tier3-paivan-kysymys';
/** 24h + 1 vk -versiot: sama tilauspolku. */
export const PAIVAN_KYSYMYS_CREDIT_PRODUCT_IDS = new Set([
  'tier3-paivan-kysymys',
  'tier4-paivan-kysymys-1vk',
]);

/** 24h tapahtumamainos mainostaululla — sähköposti + DM → /eventPromo?token= */
export const EVENT_PROMO_CREDIT_PRODUCT_ID = 'tier2-mainosta-tapahtuma';
export const EVENT_PROMO_CREDIT_PRODUCT_IDS = new Set([
  'tier2-mainosta-tapahtuma',
  'tier4-mainosta-tapahtuma-1vk',
]);

/** 24h postausmainos mainostaululla — sähköposti + DM → /postPromo?token= */
export const POST_PROMO_CREDIT_PRODUCT_ID = 'tier2-mainosta-postaus';
export const POST_PROMO_CREDIT_PRODUCT_IDS = new Set([
  'tier2-mainosta-postaus',
  'tier4-mainosta-postaus-1vk',
]);

/**
 * credit_products.id for the event main area (game / levyraati), when API omits areaUnlockCreditProductId.
 * Keep in sync with server eventAreaUnlockCreditProductId.
 */
export function getEventAreaUnlockProductId(ev) {
  if (!ev) return null;
  const et = ev.eventType ?? ev.event_type;
  if (et === 'levyraati') return 'tier4-unlock-levyraati';
  if (et === 'game') {
    const gt = ev.gameType ?? ev.game_type;
    if (gt && gt !== 'none') {
      if (gt === 'riddle-fi') return 'tier1-unlock-arvoitus';
      if (gt === 'emoji-fi') return 'tier1-unlock-emojit';
      if (gt === 'abcd-fi') return 'tier2-unlock-abcd';
    }
  }
  return null;
}

export const AVATAR_DESIGNER_BADGE_TYPES = [
  'none',
  'pallo',
  'crown',
  'p',
  'clock4',
  'eye',
  'user',
  ...ZODIAC_BADGE_TYPE_VALUES,
];

export const DEFAULT_AVATAR_DESIGNER = {
  badgeType: 'none',
  positionIndex: 0,
  iconColor: 'ffffff',
  badgeBackground: '333333',
};

/** Normalize API / DB avatar_designer JSON into a safe client shape. */
export function parseAvatarDesignerFromApi(raw) {
  const base = { ...DEFAULT_AVATAR_DESIGNER };
  let obj = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return base;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return base;
  const t = obj.badgeType ?? obj.badge_type;
  if (typeof t === 'string' && AVATAR_DESIGNER_BADGE_TYPES.includes(t)) base.badgeType = t;
  const pi = Number.parseInt(obj.positionIndex ?? obj.position_index, 10);
  if (Number.isFinite(pi) && pi >= 0 && pi <= 4) base.positionIndex = pi;
  const ic = normalizeHexTo6(obj.iconColor ?? obj.icon_color);
  const bg = normalizeHexTo6(obj.badgeBackground ?? obj.badge_background);
  if (ic) base.iconColor = ic;
  if (bg) base.badgeBackground = bg;
  return base;
}

/** Percent offsets from avatar center for badge (matches designer ring geometry). */
export function avatarDesignerBadgePositionStyle(positionIndex) {
  const pi = Number.parseInt(positionIndex, 10);
  const idx = Number.isFinite(pi) && pi >= 0 && pi <= 4 ? pi : 0;
  const rad = ((-90 + idx * 72) * Math.PI) / 180;
  const off = 41.8;
  return {
    left: `calc(50% + ${(off * Math.cos(rad)).toFixed(2)}%)`,
    top: `calc(50% + ${(off * Math.sin(rad)).toFixed(2)}%)`,
  };
}

function normalizeHexTo6(str) {
  if (str == null || typeof str !== 'string') return null;
  const s = str.replace(/^#/, '').trim();
  if (/^[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^[0-9a-fA-F]{3}$/.test(s)) return s.split('').map((c) => c + c).join('').toLowerCase();
  return null;
}
function toColorInputValue(hex6) {
  const six = normalizeHexTo6(hex6);
  return six ? `#${six}` : `#${DEFAULT_BUBBLE_COLOR_HEX}`;
}

const PROFILE_FIELDS = [
  { key: 'avatar', label: 'Avatar', type: 'image', visibilityKey: 'avatar', suomennos: 'profiilikuva' },
  { key: 'contactEmail', label: 'mail', type: 'icon', visibilityKey: 'showContactEmail', suomennos: 'sähköposti' },
  { key: 'website', label: 'globe', type: 'icon', visibilityKey: 'showWebsite', suomennos: 'verkkosivu' },
  { key: 'allowDms', label: 'yksityisviestit sallittu', type: 'yesno', visibilityKey: 'allowDms', suomennos: 'yksityisviestit sallittu' },
];
// For type 'icon', label is the Lucide icon name (lowercase, e.g. 'globe' -> Globe)

function defaultProfile() {
  return {
    avatar: 'NO',
    allowDms: 'NO',
    showDescription: 'NO',
    contactEmail: null,
    showContactEmail: 'NO',
    showWebsite: 'NO',
    showAllowDms: 'NO',
    showEventsHosted: 'YES',
    showEventRatings: 'YES'
    };
}
function profileToDisplay(profile) {
  if (!profile) return defaultProfile();
  const base = {
    ...defaultProfile(),
    ...profile,
  };
  const showDescription =
    profile.showDescription ?? profile.show_description ?? base.showDescription;
  return {
    ...base,
    avatar: profile.showAvatar ?? profile.avatar ?? 'NO',
    showAllowDms: profile.showAllowDms ?? profile.allowDms ?? 'NO',
    showDescription,
  };
}

// Avatar fallback circle: contrasting text and darker border from bubble color
function hexToRgb(hex6) {
  const h = normalizeHexTo6(hex6);
  if (!h) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
function getContrastingTextColor(hex6) {
  const rgb = hexToRgb(hex6 || DEFAULT_BUBBLE_COLOR_HEX);
  if (!rgb) return '#000000';
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}
function getDarkerBorderColor(hex6) {
  const rgb = hexToRgb(hex6 || DEFAULT_BUBBLE_COLOR_HEX);
  if (!rgb) return '#555555';
  const factor = 0.6;
  return `rgb(${Math.round(rgb.r * factor)}, ${Math.round(rgb.g * factor)}, ${Math.round(rgb.b * factor)})`;
}
function getLighterBorderColor(hex6) {
  const rgb = hexToRgb(hex6 || DEFAULT_BUBBLE_COLOR_HEX);
  if (!rgb) return '#bbbbbb';
  // Lighten towards white
  const mix = (c) => Math.round(c + (255 - c) * 0.4);
  return `rgb(${mix(rgb.r)}, ${mix(rgb.g)}, ${mix(rgb.b)})`;
}
function stringToHex(str) {
  const s = (str || '').toString();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h % 0xffffff).toString(16).padStart(6, '0');
}

function getAvatarInitials(displayName, username, maxChars = 2) {
  const name = (displayName || username || '?').trim() || '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (maxChars === 1) return (parts[0]?.[0] || name[0] || '?').toUpperCase();
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, maxChars) || '?').toUpperCase();
}

// Build emblem from display name / username:
// - Split on any non-alphanumeric characters (hyphens, underscores, punctuation, spaces, etc.)
// - Top: 2–4 capital letters from the first letter of each word that contains letters
// - Bottom: 2–4 characters from the *last* alphanumeric segment (typically numbers like an id suffix)
function getAvatarFallbackParts(displayName, username) {
  const name = (displayName || username || '').trim();
  if (!name) return null;
  // Split on any non-alphanumeric characters (including Nordic letters)
  const segments = name
    .split(/[^0-9a-zA-ZäöåÄÖÅ]+/u)
    .map((s) => s.trim())
    .filter(Boolean);

  if (segments.length === 0) return null;

  // Top part: initials from segments that contain at least one letter
  const letterSegments = segments.filter((s) => /[a-zA-ZäöåÄÖÅ]/.test(s));
  const initialsRaw = letterSegments.map((s) => s[0]).join('');
  const initials = initialsRaw.slice(0, 4).toUpperCase();

  // Bottom part: last 2–4 chars of the final alphanumeric segment
  const lastSegment = segments[segments.length - 1] || '';
  const bottomRaw = lastSegment.replace(/[^0-9a-zA-ZäöåÄÖÅ]/g, '');
  const numberPart =
    bottomRaw.length >= 4
      ? bottomRaw.slice(-4)
      : bottomRaw.length >= 2
        ? bottomRaw
        : bottomRaw || '';

  if (!initials && !numberPart) return null;

  return {
    initials: initials || (numberPart ? numberPart.slice(0, 2).toUpperCase() : ''),
    numberPart,
  };
}

function AvatarFallback({ bubbleColorHex, displayName, username, size = 'inline', className = '' }) {
  const hex = normalizeHexTo6(bubbleColorHex) || DEFAULT_BUBBLE_COLOR_HEX;
  const bg = toColorInputValue(hex);
  const border = getDarkerBorderColor(hex);
  const textColor = getContrastingTextColor(hex);
  const isSmall = size === 'small';
  const isHeader = size === 'header';
  const name = (displayName || username || '?').trim() || '?';
  const fallbackParts = !isHeader && !isSmall ? getAvatarFallbackParts(displayName, username) : null;
  const initials = getAvatarInitials(displayName, username, isSmall ? 1 : 2);
  const sizeClass =
    size === 'small'
      ? 'Profile-avatar-fallback--small'
      : size === 'large'
        ? 'Profile-avatar-fallback--large'
        : size === 'header'
          ? 'Profile-avatar-fallback--header'
          : '';
  // When we render two-part emblem, each half controls its own text color.
  // Otherwise, fall back to a single contrasting color for the whole circle.
  const baseStyle = {
    backgroundColor: bg,
    borderColor: border,
  };
  if (isHeader || !fallbackParts) {
    baseStyle.color = textColor;
  }

  // Fixed contrasting pair for emblem text: light top, dark bottom.
  const topTextColor = '#f9fafb'; // light
  const bottomTextColor = '#111827'; // dark

  return (
    <span
      className={`Profile-avatar-fallback ${sizeClass} ${className}`.trim()}
      style={baseStyle}
      aria-hidden
    >
      {isHeader ? name : fallbackParts ? (
        <span className="Profile-avatar-fallback-inner">
          <span
            className="Profile-avatar-fallback-initials"
            style={{ color: topTextColor }}
          >
            {fallbackParts.initials}
          </span>
          <span
            className="Profile-avatar-fallback-numbers"
            style={{ color: bottomTextColor }}
          >
            {fallbackParts.numberPart}
          </span>
        </span>
      ) : (
        initials
      )}
    </span>
  );
}

export {
  API,
  DEFAULT_BUBBLE_COLOR_HEX,
  normalizeHexTo6,
  toColorInputValue,
  stringToHex,
  PROFILE_FIELDS,
  defaultProfile,
  profileToDisplay,
  getContrastingTextColor,
  getDarkerBorderColor,
  getLighterBorderColor,
  getAvatarInitials,
  getAvatarFallbackParts,
  AvatarFallback,
};
